import {jsx} from 'panel';

import {InputComponent, InputEl} from 'common/components/input';
import {parseNumber} from 'common/util';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {InputChangeEvent} from 'common/components/input/types';

import type {FilterEntryRow} from '../index';

const MULTIPLE_VALUES_OPERATORS = new Set<NumberOperator>([NumberOperator.Between, NumberOperator.NotBetween]);

export function FilterNumericValue(this: FilterEntryRow) {
  const {filterValue, filterOperator} = this.filter;

  const inputEl = (currentValue: number, index: number, handleInput: (number) => void) => {
    const isInvalid = parseNumber(currentValue) === null;
    const shouldAutoFocus =
      isInvalid &&
      // auto-focus on first invalid field
      (Array.isArray(filterValue) ? index === filterValue.findIndex((value) => value === null) ?? 0 : index === 0);
    const value = currentValue?.toString(10) ?? ``;

    return (
      <InputEl
        key={`input-${index}-${filterOperator}`}
        class={{'is-placeholder': isInvalid}}
        attrs={{
          elref: `filterInput`,
          type: `number`,
          placeholder: `value`,
          'select-on-focus': true,
        }}
        props={{value}}
        hook={shouldAutoFocus && {insert: (node) => requestAnimationFrame(() => (node.elm as InputComponent).focus())}}
        on={{
          change: (ev: InputChangeEvent) => {
            // when the upadted value is not a valid input, revert back to previously committed input
            const updatedValue = parseNumber(ev.detail.value);
            if (updatedValue !== null) {
              handleInput(updatedValue);
            } else {
              (ev.target as HTMLInputElement).value = value;
            }
          },
        }}
      />
    );
  };

  // handle cases where the numeric filter requires 2 values
  if (MULTIPLE_VALUES_OPERATORS.has(this.filter.filterOperator as NumberOperator)) {
    const [minValue, maxValue] = this.filter.filterValue as number[];
    return (
      <>
        {inputEl(minValue, 0, (value) => this.handleFilterValueChange([value, maxValue]))}
        <div sel='.entry-filter-conjunction'>and</div>
        {inputEl(maxValue, 1, (value) => this.handleFilterValueChange([minValue, value]))}
      </>
    );
  }

  return <>{inputEl(this.filter.filterValue as number, 0, (value) => this.handleFilterValueChange(value))}</>;
}
